import { useEffect, useMemo, useState } from "react";
import {
  Archive,
  Camera,
  Check,
  Edit3,
  ImagePlus,
  LayoutGrid,
  List,
  PackageOpen,
  Plus,
  Search,
  SlidersHorizontal,
  Sparkles,
  Star,
  Trash2,
  X,
} from "lucide-react";
import { accountApi, accountFormApi, type TackleItem } from "../../account";

const types: Record<TackleItem["tackle_type"], string> = {
  wobbler: "Vaappu",
  spinner: "Lippa",
  spoon: "Lusikka",
  jig: "Jigi",
  fly: "Perho",
  hook: "Koukku",
  other: "Muu",
};
const empty = {
  name: "",
  tackleType: "wobbler",
  brand: "",
  model: "",
  productCode: "",
  color: "",
  lengthCm: "",
  weightG: "",
  quantity: "1",
  targetSpecies: "",
  notes: "",
  favorite: false,
};
type Draft = typeof empty;
type RecognitionProposal = {
  field: "tackleType" | "brand" | "model" | "color" | "targetSpecies";
  value: string | string[];
  confidence: number;
  rationale: string;
};
type Recognition = {
  status?: string;
  description?: string;
  proposals?: RecognitionProposal[];
  decision?: { action: string; appliedFields: string[] };
};
const recognitionLabels: Record<RecognitionProposal["field"], string> = {
  tackleType: "Tyyppi",
  brand: "Merkki",
  model: "Malli",
  color: "Väri",
  targetSpecies: "Tavoitelajit",
};
const recognitionValue = (proposal: RecognitionProposal) =>
  proposal.field === "tackleType"
    ? (types[proposal.value as TackleItem["tackle_type"]] ??
      String(proposal.value))
    : Array.isArray(proposal.value)
      ? proposal.value.join(", ")
      : proposal.value;
const parseRecognition = (item: TackleItem | null): Recognition => {
  try {
    return JSON.parse(item?.recognition_json ?? "{}") as Recognition;
  } catch {
    return {};
  }
};
const itemDraft = (item: TackleItem): Draft => ({
  name: item.name,
  tackleType: item.tackle_type,
  brand: item.brand,
  model: item.model,
  productCode: item.product_code,
  color: item.color,
  lengthCm: item.length_mm ? String(item.length_mm / 10) : "",
  weightG: item.weight_g ? String(item.weight_g) : "",
  quantity: String(item.quantity),
  targetSpecies: (JSON.parse(item.target_species_json) as string[]).join(", "),
  notes: item.notes,
  favorite: Boolean(item.favorite),
});
const imageUrl = (item: TackleItem) =>
  `/api/account/tackle-items/${encodeURIComponent(item.id)}/image?v=${encodeURIComponent(item.updated_at)}`;

export function TackleBoxPage() {
  const [items, setItems] = useState<TackleItem[]>([]),
    [loading, setLoading] = useState(true),
    [saving, setSaving] = useState(false),
    [recognizing, setRecognizing] = useState(false),
    [recognitionSeconds, setRecognitionSeconds] = useState(0),
    [message, setMessage] = useState(""),
    [query, setQuery] = useState(""),
    [type, setType] = useState("all"),
    [showArchived, setShowArchived] = useState(false),
    [favoritesOnly, setFavoritesOnly] = useState(false),
    [sort, setSort] = useState("updated"),
    [view, setView] = useState<"grid" | "list">("grid"),
    [selectedItems, setSelectedItems] = useState<string[]>([]),
    [editing, setEditing] = useState<string | null>(null),
    [open, setOpen] = useState(false),
    [draft, setDraft] = useState<Draft>(empty),
    [photo, setPhoto] = useState<File | null>(null),
    [photoPreview, setPhotoPreview] = useState(""),
    [removePhoto, setRemovePhoto] = useState(false),
    [photoError, setPhotoError] = useState(""),
    [selectedRecognition, setSelectedRecognition] = useState<string[]>([]),
    [draftRecognition, setDraftRecognition] = useState<Recognition | null>(
      null,
    ),
    [selectedDraftRecognition, setSelectedDraftRecognition] = useState<
      string[]
    >([]);
  const load = async () => {
    setLoading(true);
    try {
      const value = await accountApi<{ items: TackleItem[] }>(
        "/api/account/tackle-items?archived=1",
      );
      setItems(value.items);
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Kalapakkia ei voitu ladata.",
      );
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  useEffect(
    () => () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    },
    [photoPreview],
  );
  useEffect(() => {
    if (!recognizing) {
      setRecognitionSeconds(0);
      return;
    }
    const startedAt = Date.now(),
      timer = window.setInterval(
        () =>
          setRecognitionSeconds(Math.floor((Date.now() - startedAt) / 1000)),
        1000,
      );
    return () => window.clearInterval(timer);
  }, [recognizing]);
  const visibleItems = showArchived
    ? items
    : items.filter((item) => item.status === "active");
  const filtered = useMemo(() => {
    const result = visibleItems.filter(
        (item) =>
          (type === "all" || item.tackle_type === type) &&
          (!favoritesOnly || Boolean(item.favorite)) &&
          (!query.trim() ||
            [
              item.name,
              item.brand,
              item.model,
              item.color,
              item.target_species_json,
            ]
              .join(" ")
              .toLocaleLowerCase("fi")
              .includes(query.trim().toLocaleLowerCase("fi"))),
      );
    return result.sort((left, right) => {
      if (sort === "name") return left.name.localeCompare(right.name, "fi");
      if (sort === "quantity") return right.quantity - left.quantity;
      if (sort === "favorite")
        return Number(right.favorite) - Number(left.favorite) ||
          left.name.localeCompare(right.name, "fi");
      return Date.parse(right.updated_at) - Date.parse(left.updated_at);
    });
  }, [visibleItems, query, type, favoritesOnly, sort]);
  const active = items.filter((item) => item.status === "active"),
    favorites = active.filter((item) => item.favorite).length,
    total = active.reduce((sum, item) => sum + item.quantity, 0),
    current = editing
      ? (items.find((item) => item.id === editing) ?? null)
      : null,
    recognition = parseRecognition(current),
    recognitionProposals = recognition.proposals ?? [],
    draftRecognitionProposals = draftRecognition?.proposals ?? [];
  const normalized = (value: string) =>
    value.trim().toLocaleLowerCase("fi").replace(/\s+/g, " ");
  const duplicateCandidates = useMemo(
    () =>
      items
        .filter((item) => item.id !== editing)
        .filter((item) => {
          const draftColors = normalized(draft.color)
            .split(/[\s/,+-]+/)
            .filter((value) => value.length >= 3);
          const itemColors = normalized(item.color)
            .split(/[\s/,+-]+/)
            .filter((value) => value.length >= 3);
          const sharedColor = draftColors.some((value) =>
            itemColors.some(
              (candidate) =>
                candidate === value ||
                (value.length >= 4 && candidate.includes(value)) ||
                (candidate.length >= 4 && value.includes(candidate)),
            ),
          );
          const exactName =
            normalized(draft.name).length >= 3 &&
            normalized(item.name) === normalized(draft.name);
          const sameProduct =
            normalized(draft.brand).length >= 2 &&
            normalized(draft.model).length >= 2 &&
            normalized(item.brand) === normalized(draft.brand) &&
            normalized(item.model) === normalized(draft.model);
          const sameProfile =
            sharedColor &&
            item.tackle_type === draft.tackleType &&
            (!draft.lengthCm ||
              !item.length_mm ||
              Math.abs(item.length_mm - Number(draft.lengthCm) * 10) <= 5);
          return exactName || sameProduct || sameProfile;
        })
        .slice(0, 3),
    [draft, editing, items],
  );
  const resetPhoto = () => {
    setPhoto(null);
    setPhotoPreview("");
    setRemovePhoto(false);
    setPhotoError("");
    setDraftRecognition(null);
    setSelectedDraftRecognition([]);
  };
  const close = () => {
    setOpen(false);
    setEditing(null);
    setDraft(empty);
    setSelectedRecognition([]);
    setDraftRecognition(null);
    setSelectedDraftRecognition([]);
    resetPhoto();
  };
  const openNew = () => {
    setEditing(null);
    setDraft(empty);
    setSelectedRecognition([]);
    setDraftRecognition(null);
    setSelectedDraftRecognition([]);
    resetPhoto();
    setOpen(true);
  };
  const openEdit = (item: TackleItem) => {
    setEditing(item.id);
    setDraft(itemDraft(item));
    setSelectedRecognition(
      parseRecognition(item).proposals?.map((proposal) => proposal.field) ?? [],
    );
    resetPhoto();
    setOpen(true);
  };
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !saving && !recognizing) close();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open, saving, recognizing]);
  const choosePhoto = (file: File | null) => {
    setPhotoError("");
    if (!file) return;
    if (
      !["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
      file.size > 6 * 1024 * 1024
    ) {
      setPhotoError("Valitse enintään 6 Mt:n JPEG-, PNG- tai WebP-kuva.");
      return;
    }
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
    setRemovePhoto(false);
    setDraftRecognition(null);
    setSelectedDraftRecognition([]);
  };
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    const body = {
      name: draft.name,
      tackleType: draft.tackleType,
      brand: draft.brand,
      model: draft.model,
      productCode: draft.productCode,
      color: draft.color,
      lengthMm: draft.lengthCm ? Math.round(Number(draft.lengthCm) * 10) : null,
      weightG: draft.weightG ? Number(draft.weightG) : null,
      quantity: Number(draft.quantity),
      targetSpecies: draft.targetSpecies
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean),
      notes: draft.notes,
      favorite: draft.favorite,
    };
    let saved: TackleItem;
    try {
      const result = await accountApi<{ item: TackleItem }>(
        editing
          ? `/api/account/tackle-items/${encodeURIComponent(editing)}`
          : "/api/account/tackle-items",
        { method: editing ? "PATCH" : "POST", body: JSON.stringify(body) },
      );
      saved = result.item;
      if (!editing) setEditing(saved.id);
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Välinettä ei voitu tallentaa.",
      );
      setSaving(false);
      return;
    }
    try {
      if (photo) {
        const form = new FormData();
        form.set("image", photo);
        await accountFormApi(
          `/api/account/tackle-items/${encodeURIComponent(saved.id)}/image`,
          form,
        );
      } else if (removePhoto && current?.image_id)
        await accountApi(
          `/api/account/tackle-items/${encodeURIComponent(saved.id)}/image`,
          { method: "DELETE" },
        );
    } catch (error) {
      setMessage(
        `Väline tallennettiin, mutta kuvaa ei voitu tallentaa: ${error instanceof Error ? error.message : "tuntematon virhe"}`,
      );
      await load();
      setSaving(false);
      return;
    }
    setMessage(
      editing
        ? "Väline päivitettiin."
        : photo
          ? "Väline ja kuva lisättiin Kalapakkiin."
          : "Väline lisättiin Kalapakkiin.",
    );
    close();
    await load();
    setSaving(false);
  };
  const action = async (
    item: TackleItem,
    next: "favorite" | "archive" | "restore" | "delete",
  ) => {
    setMessage("");
    try {
      if (next === "delete") {
        if (!window.confirm(`Poistetaanko ${item.name} pysyvästi?`)) return;
        await accountApi(
          `/api/account/tackle-items/${encodeURIComponent(item.id)}`,
          { method: "DELETE" },
        );
      } else
        await accountApi(
          `/api/account/tackle-items/${encodeURIComponent(item.id)}`,
          {
            method: "PATCH",
            body: JSON.stringify(
              next === "favorite"
                ? { favorite: !item.favorite }
                : { action: next },
            ),
          },
        );
      await load();
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Muutosta ei voitu tallentaa.",
      );
    }
  };
  const recognize = async () => {
    if (!current) return;
    setRecognizing(true);
    setPhotoError("");
    try {
      const result = await accountApi<{ recognition: Recognition }>(
        `/api/account/tackle-items/${encodeURIComponent(current.id)}/recognition`,
        { method: "POST" },
      );
      setSelectedRecognition(
        result.recognition.proposals?.map((proposal) => proposal.field) ?? [],
      );
      await load();
    } catch (error) {
      setPhotoError(
        error instanceof Error ? error.message : "Kuvaa ei voitu tunnistaa.",
      );
    } finally {
      setRecognizing(false);
    }
  };
  const recognizeDraft = async () => {
    if (!photo) return;
    setRecognizing(true);
    setPhotoError("");
    try {
      const form = new FormData();
      form.set("image", photo);
      const result = await accountFormApi<{ recognition: Recognition }>(
        "/api/account/tackle-recognition",
        form,
      );
      setDraftRecognition(result.recognition);
      setSelectedDraftRecognition(
        result.recognition.proposals?.map((proposal) => proposal.field) ?? [],
      );
    } catch (error) {
      setPhotoError(
        error instanceof Error ? error.message : "Kuvaa ei voitu tunnistaa.",
      );
    } finally {
      setRecognizing(false);
    }
  };
  const applyDraftRecognition = () => {
    const selected = draftRecognitionProposals.filter((proposal) =>
      selectedDraftRecognition.includes(proposal.field),
    );
    const next = { ...draft };
    for (const proposal of selected) {
      if (proposal.field === "tackleType")
        next.tackleType = String(proposal.value);
      if (proposal.field === "brand") next.brand = String(proposal.value);
      if (proposal.field === "model") next.model = String(proposal.value);
      if (proposal.field === "color") next.color = String(proposal.value);
      if (proposal.field === "targetSpecies")
        next.targetSpecies = Array.isArray(proposal.value)
          ? proposal.value.join(", ")
          : String(proposal.value);
    }
    if (!next.name.trim()) {
      const typeLabel =
        types[next.tackleType as TackleItem["tackle_type"]] ?? "Väline";
      next.name = next.color.trim()
        ? `${next.color} ${typeLabel.toLocaleLowerCase("fi")}`
        : typeLabel;
    }
    setDraft(next);
    setDraftRecognition(null);
    setSelectedDraftRecognition([]);
  };
  const decideRecognition = async (action: "confirm" | "reject") => {
    if (!current) return;
    setRecognizing(true);
    setPhotoError("");
    try {
      const result = await accountApi<{ item?: TackleItem }>(
        `/api/account/tackle-items/${encodeURIComponent(current.id)}/recognition`,
        {
          method: "PATCH",
          body: JSON.stringify({ action, fields: selectedRecognition }),
        },
      );
      if (result.item) setDraft(itemDraft(result.item));
      setSelectedRecognition([]);
      await load();
    } catch (error) {
      setPhotoError(
        error instanceof Error
          ? error.message
          : "Ehdotusta ei voitu käsitellä.",
      );
    } finally {
      setRecognizing(false);
    }
  };
  const bulkAction = async (next: "archive" | "delete") => {
    const chosen = items.filter((item) => selectedItems.includes(item.id));
    if (!chosen.length) return;
    if (
      next === "delete" &&
      !window.confirm(
        `Poistetaanko ${chosen.length} valittua välinettä pysyvästi?`,
      )
    )
      return;
    setSaving(true);
    setMessage("");
    try {
      await accountApi("/api/account/tackle-items/bulk", {
        method: "POST",
        body: JSON.stringify({
          action: next,
          ids: chosen.map((item) => item.id),
        }),
      });
      setSelectedItems([]);
      setMessage(
        next === "delete"
          ? `${chosen.length} välinettä poistettiin.`
          : `${chosen.length} välinettä arkistoitiin.`,
      );
      await load();
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Valittuja välineitä ei voitu käsitellä.",
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="tackle-page">
      <section className="tackle-overview">
        <div className="tackle-overview-copy">
          <span className="eyebrow">Kalapakin sisältö</span>
          <h2>Varusteet valmiina seuraavalle reissulle</h2>
          <p>
            Pidä käyttämäsi vieheet järjestyksessä. Tavoitelajit auttavat
            myöhemmin reissun pakkaamisessa, mutta eivät muuta lupa-arviota.
          </p>
          <button onClick={openNew}>
            <Plus />
            Lisää uusi väline
          </button>
        </div>
        <div className="tackle-stats">
          <article>
            <PackageOpen />
            <span>
              <b>{total}</b>
              <small>välinettä</small>
            </span>
          </article>
          <article>
            <Star />
            <span>
              <b>{favorites}</b>
              <small>suosikkia</small>
            </span>
          </article>
          <article>
            <Archive />
            <span>
              <b>{items.filter((item) => item.status === "archived").length}</b>
              <small>arkistossa</small>
            </span>
          </article>
        </div>
      </section>
      {message && (
        <div className="account-inline-message" role="status">
          {message}
        </div>
      )}
      {open && (
        <div
          className="tackle-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tackle-form-title"
        >
          <button
            className="tackle-modal-backdrop"
            aria-label="Sulje välinelomake"
            onClick={close}
          />
          <form className="tackle-form" onSubmit={save}>
            <header>
              <div>
                <span className="eyebrow">
                  {editing ? "Muokkaa välinettä" : "Uusi väline"}
                </span>
                <h3 id="tackle-form-title">
                  {editing ? draft.name : "Lisää Kalapakkiin"}
                </h3>
              </div>
              <button type="button" aria-label="Sulje" onClick={close}>
                <X />
              </button>
            </header>
            <div className="tackle-form-content">
            <div className="tackle-photo-editor">
              <div
                className={`tackle-photo-frame${photoPreview || (current?.image_id && !removePhoto) ? " has-image" : ""}`}
              >
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Valitun välinekuvan esikatselu"
                  />
                ) : current?.image_id && !removePhoto ? (
                  <img
                    src={imageUrl(current)}
                    alt={`${current.name} Kalapakissa`}
                  />
                ) : (
                  <>
                    <Camera />
                    <span>
                      <b>Lisää välineestä kuva</b>
                      <small>JPEG, PNG tai WebP · enintään 6 Mt</small>
                    </span>
                  </>
                )}
              </div>
              <div>
                <label className="tackle-photo-button">
                  <ImagePlus />
                  {photoPreview || (current?.image_id && !removePhoto)
                    ? "Vaihda kuva"
                    : "Valitse kuva"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(event) =>
                      choosePhoto(event.target.files?.[0] ?? null)
                    }
                  />
                </label>
                {(photoPreview || (current?.image_id && !removePhoto)) && (
                  <button
                    type="button"
                    onClick={() => {
                      setPhoto(null);
                      setPhotoPreview("");
                      setRemovePhoto(Boolean(current?.image_id));
                      setDraftRecognition(null);
                      setSelectedDraftRecognition([]);
                    }}
                  >
                    Poista kuva
                  </button>
                )}
                <small>
                  Kuva on yksityinen. Tunnistus ei muuta tietoja ilman
                  hyväksyntääsi.
                </small>
                {photoError && <em role="alert">{photoError}</em>}
              </div>
            </div>
            {!editing && photo && (
              <section
                className="tackle-recognition-panel is-draft"
                aria-label="Uuden välineen kuvatunnistus"
              >
                <header>
                  <span>
                    <Sparkles />
                    <b>Täytä tiedot kuvasta</b>
                  </span>
                  <small>Esikatselu ennen tallennusta</small>
                </header>
                {draftRecognition ? (
                  <>
                    {draftRecognitionProposals.length ? (
                      <div className="tackle-recognition-list">
                        {draftRecognitionProposals.map((proposal) => (
                          <label key={proposal.field}>
                            <input
                              type="checkbox"
                              checked={selectedDraftRecognition.includes(
                                proposal.field,
                              )}
                              onChange={(event) =>
                                setSelectedDraftRecognition(
                                  event.target.checked
                                    ? [
                                        ...selectedDraftRecognition,
                                        proposal.field,
                                      ]
                                    : selectedDraftRecognition.filter(
                                        (field) => field !== proposal.field,
                                      ),
                                )
                              }
                            />
                            <span>
                              <b>{recognitionLabels[proposal.field]}</b>
                              <strong>{recognitionValue(proposal)}</strong>
                              <small>
                                {Math.round(proposal.confidence * 100)} %
                                varmuus
                                {proposal.rationale
                                  ? ` · ${proposal.rationale}`
                                  : ""}
                              </small>
                            </span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p>
                        Kuvasta ei löytynyt riittävän varmoja ehdotuksia. Voit
                        täyttää tiedot käsin.
                      </p>
                    )}
                    <div className="tackle-recognition-actions">
                      <button
                        type="button"
                        onClick={() => {
                          setDraftRecognition(null);
                          setSelectedDraftRecognition([]);
                        }}
                      >
                        Ohita ehdotus
                      </button>
                      {draftRecognitionProposals.length > 0 && (
                        <button
                          type="button"
                          disabled={!selectedDraftRecognition.length}
                          onClick={applyDraftRecognition}
                        >
                          <Check />
                          Täytä valitut
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="tackle-recognition-intro">
                    <p>
                      AI ehdottaa kuvasta vain näkyviä tietoja. Tarkistat ja
                      hyväksyt kentät ennen kuin väline tallennetaan.
                    </p>
                    <button
                      className="tackle-recognize-button"
                      type="button"
                      disabled={recognizing}
                      onClick={() => void recognizeDraft()}
                    >
                      <Sparkles />
                      {recognizing
                        ? `Analysoidaan kuvaa · ${recognitionSeconds} s`
                        : "Tunnista ennen tallennusta"}
                    </button>
                  </div>
                )}
              </section>
            )}
            {current?.image_id && !photo && !removePhoto && (
              <section
                className="tackle-recognition-panel"
                aria-label="Kuvan AI-tunnistus"
              >
                <header>
                  <span>
                    <Sparkles />
                    <b>Kuvatunnistus</b>
                  </span>
                  <small>AI ehdottaa, sinä päätät</small>
                </header>
                {current.recognition_status === "suggested" ? (
                  <>
                    {recognitionProposals.length ? (
                      <div className="tackle-recognition-list">
                        {recognitionProposals.map((proposal) => (
                          <label key={proposal.field}>
                            <input
                              type="checkbox"
                              checked={selectedRecognition.includes(
                                proposal.field,
                              )}
                              onChange={(event) =>
                                setSelectedRecognition(
                                  event.target.checked
                                    ? [...selectedRecognition, proposal.field]
                                    : selectedRecognition.filter(
                                        (field) => field !== proposal.field,
                                      ),
                                )
                              }
                            />
                            <span>
                              <b>{recognitionLabels[proposal.field]}</b>
                              <strong>{recognitionValue(proposal)}</strong>
                              <small>
                                {Math.round(proposal.confidence * 100)} %
                                varmuus
                                {proposal.rationale
                                  ? ` · ${proposal.rationale}`
                                  : ""}
                              </small>
                            </span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p>
                        Kuvasta ei löytynyt riittävän varmoja
                        täydennysehdotuksia. Nykyisiä tietoja ei muutettu.
                      </p>
                    )}
                    <div className="tackle-recognition-actions">
                      <button
                        type="button"
                        disabled={recognizing}
                        onClick={() => void decideRecognition("reject")}
                      >
                        Hylkää ehdotus
                      </button>
                      {recognitionProposals.length > 0 && (
                        <button
                          type="button"
                          disabled={recognizing || !selectedRecognition.length}
                          onClick={() => void decideRecognition("confirm")}
                        >
                          <Check />
                          Hyväksy valitut
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="tackle-recognition-intro">
                    <p>
                      Tunnistus voi ehdottaa tyyppiä, väriä, merkkiä, mallia ja
                      tavoitelajeja. Mitään ei täytetä automaattisesti.
                    </p>
                    <button
                      className="tackle-recognize-button"
                      type="button"
                      disabled={recognizing}
                      onClick={() => void recognize()}
                    >
                      <Sparkles />
                      {recognizing
                        ? `Analysoidaan kuvaa · ${recognitionSeconds} s`
                        : "Tunnista kuvasta"}
                    </button>
                    {recognizing && (
                      <small
                        className="tackle-recognition-progress"
                        role="status"
                      >
                        Kuvasta muodostetaan turvallinen kuvaus ja
                        kenttäehdotukset. Mitään ei tallenneta automaattisesti.
                      </small>
                    )}
                  </div>
                )}
              </section>
            )}
            {duplicateCandidates.length > 0 && (
              <aside className="tackle-duplicate-warning" role="status">
                <strong>Samankaltainen väline saattaa olla jo pakissa</strong>
                <p>
                  Tarkista ennen tallennusta, ettei samasta vieheestä synny
                  vahingossa kahta korttia.
                </p>
                <div>
                  {duplicateCandidates.map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      onClick={() => openEdit(item)}
                    >
                      {item.name} · {types[item.tackle_type]} · {item.quantity}{" "}
                      kpl
                    </button>
                  ))}
                </div>
              </aside>
            )}
            <section className="tackle-form-section is-basics">
              <header><span>1</span><div><b>Välineen perustiedot</b><small>Nimi ja tyyppi riittävät alkuun. Muut tiedot helpottavat hakua.</small></div></header>
              <div className="tackle-form-grid">
              <label>
                Nimi
                <input
                  value={draft.name}
                  onChange={(event) =>
                    setDraft({ ...draft, name: event.target.value })
                  }
                  maxLength={120}
                  placeholder="Esimerkiksi Countdown 7"
                  required
                  autoFocus
                />
              </label>
              <label>
                Tyyppi
                <select
                  value={draft.tackleType}
                  onChange={(event) =>
                    setDraft({ ...draft, tackleType: event.target.value })
                  }
                >
                  {Object.entries(types).map(([value, label]) => (
                    <option value={value} key={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Merkki
                <input
                  value={draft.brand}
                  onChange={(event) =>
                    setDraft({ ...draft, brand: event.target.value })
                  }
                  maxLength={80}
                  placeholder="Rapala"
                />
              </label>
              <label>
                Malli
                <input
                  value={draft.model}
                  onChange={(event) =>
                    setDraft({ ...draft, model: event.target.value })
                  }
                  maxLength={100}
                />
              </label>
              <label>
                Väri
                <input
                  value={draft.color}
                  onChange={(event) =>
                    setDraft({ ...draft, color: event.target.value })
                  }
                  maxLength={80}
                  placeholder="Hopea / sininen"
                />
              </label>
              <label>
                Tuotetunniste
                <input
                  value={draft.productCode}
                  onChange={(event) =>
                    setDraft({ ...draft, productCode: event.target.value })
                  }
                  maxLength={100}
                />
              </label>
              </div>
            </section>
            <section className="tackle-form-section is-usage">
              <header><span>2</span><div><b>Mitat ja käyttö</b><small>Määrä vaikuttaa pakkaamiseen. Tavoitelajit ovat vain omia muistiinpanojasi.</small></div></header>
              <div className="tackle-form-grid">
              <label>
                Pituus, cm
                <input
                  type="number"
                  min="0.1"
                  max="200"
                  step="0.1"
                  value={draft.lengthCm}
                  onChange={(event) =>
                    setDraft({ ...draft, lengthCm: event.target.value })
                  }
                />
              </label>
              <label>
                Paino, g
                <input
                  type="number"
                  min="0.01"
                  max="10000"
                  step="0.01"
                  value={draft.weightG}
                  onChange={(event) =>
                    setDraft({ ...draft, weightG: event.target.value })
                  }
                />
              </label>
              <label>
                Määrä
                <input
                  type="number"
                  min="0"
                  max="999"
                  value={draft.quantity}
                  onChange={(event) =>
                    setDraft({ ...draft, quantity: event.target.value })
                  }
                  required
                />
              </label>
              <label className="wide">
                Tavoitelajit <small>erota pilkulla</small>
                <input
                  value={draft.targetSpecies}
                  onChange={(event) =>
                    setDraft({ ...draft, targetSpecies: event.target.value })
                  }
                  placeholder="Ahven, kuha"
                />
              </label>
              <label className="wide">
                Omat muistiinpanot
                <textarea
                  value={draft.notes}
                  onChange={(event) =>
                    setDraft({ ...draft, notes: event.target.value })
                  }
                  rows={3}
                  maxLength={1000}
                />
              </label>
              <label className="tackle-favorite">
                <input
                  type="checkbox"
                  checked={draft.favorite}
                  onChange={(event) =>
                    setDraft({ ...draft, favorite: event.target.checked })
                  }
                />
                <Star />
                Merkitse suosikiksi
              </label>
              </div>
            </section>
            </div>
            <footer>
              <button type="button" onClick={close}>
                Peruuta
              </button>
              <button disabled={saving}>
                {saving
                  ? "Tallennetaan…"
                  : editing
                    ? "Tallenna muutokset"
                    : "Lisää Kalapakkiin"}
              </button>
            </footer>
          </form>
        </div>
      )}
      <section className="tackle-inventory">
        <header>
          <div>
            <span className="eyebrow">Omat välineet</span>
            <h3>
              {filtered.length} {filtered.length === 1 ? "väline" : "välinettä"}
            </h3>
          </div>
          <SlidersHorizontal />
        </header>
        <div className="tackle-toolbar">
          <label>
            <Search />
            <input
              aria-label="Hae Kalapakista"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Hae nimellä, värillä tai lajilla"
            />
          </label>
          <select
            aria-label="Suodata tyypillä"
            value={type}
            onChange={(event) => setType(event.target.value)}
          >
            <option value="all">Kaikki tyypit</option>
            {Object.entries(types).map(([value, label]) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            aria-label="Järjestä välineet"
            value={sort}
            onChange={(event) => setSort(event.target.value)}
          >
            <option value="updated">Viimeksi muokatut</option>
            <option value="name">Nimen mukaan</option>
            <option value="quantity">Määrän mukaan</option>
            <option value="favorite">Suosikit ensin</option>
          </select>
          <label className="tackle-archived">
            <input
              type="checkbox"
              checked={favoritesOnly}
              onChange={(event) => setFavoritesOnly(event.target.checked)}
            />
            Vain suosikit
          </label>
          <label className="tackle-archived">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(event) => setShowArchived(event.target.checked)}
            />
            Näytä arkistoidut
          </label>
          <div className="tackle-view-toggle" aria-label="Näkymä">
            <button
              type="button"
              className={view === "grid" ? "active" : undefined}
              aria-label="Ruudukkonäkymä"
              onClick={() => setView("grid")}
            >
              <LayoutGrid />
            </button>
            <button
              type="button"
              className={view === "list" ? "active" : undefined}
              aria-label="Listanäkymä"
              onClick={() => setView("list")}
            >
              <List />
            </button>
          </div>
        </div>
        {filtered.length > 0 && (
          <div className="tackle-bulk-toolbar">
            <label>
              <input
                type="checkbox"
                checked={
                  filtered.length > 0 &&
                  filtered.every((item) => selectedItems.includes(item.id))
                }
                onChange={(event) =>
                  setSelectedItems(
                    event.target.checked
                      ? [...new Set([...selectedItems, ...filtered.map((item) => item.id)])]
                      : selectedItems.filter(
                          (id) => !filtered.some((item) => item.id === id),
                        ),
                  )
                }
              />
              Valitse näkyvät
            </label>
            {selectedItems.length > 0 && (
              <span>
                <b>{selectedItems.length} valittu</b>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void bulkAction("archive")}
                >
                  <Archive /> Arkistoi
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void bulkAction("delete")}
                >
                  <Trash2 /> Poista
                </button>
              </span>
            )}
          </div>
        )}
      </section>
      {loading ? (
        <div className="account-loading">Ladataan Kalapakkia…</div>
      ) : filtered.length ? (
        <div className={`tackle-grid ${view === "list" ? "is-list" : ""}`}>
          {filtered.map((item) => {
            const species = JSON.parse(item.target_species_json) as string[];
            return (
              <article
                className={`${item.status} type-${item.tackle_type}${selectedItems.includes(item.id) ? " selected" : ""}`}
                key={item.id}
                id={item.id}
              >
                <div
                  className={`tackle-card-visual${item.image_id ? " has-photo" : ""}`}
                >
                  <span className="tackle-type">{types[item.tackle_type]}</span>
                  <label className="tackle-card-select">
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(item.id)}
                      aria-label={`Valitse ${item.name}`}
                      onChange={(event) =>
                        setSelectedItems(
                          event.target.checked
                            ? [...selectedItems, item.id]
                            : selectedItems.filter((id) => id !== item.id),
                        )
                      }
                    />
                  </label>
                  <button
                    aria-label={`${item.favorite ? "Poista suosikeista" : "Lisää suosikiksi"} ${item.name}`}
                    onClick={() => void action(item, "favorite")}
                  >
                    <Star className={item.favorite ? "filled" : undefined} />
                  </button>
                  {item.image_id ? (
                    <img
                      src={imageUrl(item)}
                      alt={`${item.name} Kalapakissa`}
                      loading="lazy"
                    />
                  ) : (
                    <>
                      <PackageOpen />
                      <small>Lisää oma kuva</small>
                    </>
                  )}
                </div>
                <div className="tackle-card-copy">
                  <small>
                    {[item.brand, item.model].filter(Boolean).join(" · ") ||
                      "OMA VÄLINE"}
                  </small>
                  <h3>{item.name}</h3>
                  <p>
                    {[
                      item.color,
                      item.length_mm ? `${item.length_mm / 10} cm` : "",
                      item.weight_g ? `${item.weight_g} g` : "",
                    ]
                      .filter(Boolean)
                      .join(" · ") || "Täydennä väri ja koko"}
                  </p>
                  {species.length > 0 && (
                    <div>
                      {species.map((value) => (
                        <span key={value}>{value}</span>
                      ))}
                    </div>
                  )}
                </div>
                <dl>
                  <div>
                    <dt>Määrä</dt>
                    <dd>{item.quantity} kpl</dd>
                  </div>
                  <div>
                    <dt>Tila</dt>
                    <dd>
                      {item.status === "active" ? "Käytössä" : "Arkistoitu"}
                    </dd>
                  </div>
                  <div>
                    <dt>Omat saaliit</dt>
                    <dd>{Number(item.catch_count ?? 0)} kpl</dd>
                  </div>
                  {item.top_catch_place&&<div><dt>Missä kirjattu?</dt><dd>{item.top_catch_place} · {Number(item.top_catch_place_count??0)} kalaa</dd></div>}
                </dl>
                {item.notes && <p className="tackle-notes">{item.notes}</p>}
                <footer>
                  {Number(item.catch_count ?? 0)>0&&<a href={`/oma/saaliit?tackle=${encodeURIComponent(item.id)}`}>Näytä saaliit</a>}
                  <button onClick={() => openEdit(item)}>
                    <Edit3 />
                    Muokkaa
                  </button>
                  <button
                    onClick={() =>
                      void action(
                        item,
                        item.status === "active" ? "archive" : "restore",
                      )
                    }
                  >
                    <Archive />
                    {item.status === "active" ? "Arkistoi" : "Palauta"}
                  </button>
                  <button
                    className="danger"
                    aria-label={`Poista ${item.name}`}
                    onClick={() => void action(item, "delete")}
                  >
                    <Trash2 />
                  </button>
                </footer>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="tackle-empty">
          <span>
            <PackageOpen />
          </span>
          <h3>
            {items.length
              ? "Hakuehdoilla ei löytynyt välineitä"
              : "Rakenna oma Kalapakkisi"}
          </h3>
          <p>
            {items.length
              ? "Kokeile toista hakusanaa tai poista suodatin."
              : "Lisää ensimmäinen viehe. Kun pakki kasvaa, voit hakea välineitä lajin, värin ja tyypin perusteella."}
          </p>
          {!items.length && (
            <button onClick={openNew}>
              <Plus />
              Lisää ensimmäinen väline
            </button>
          )}
        </div>
      )}
    </section>
  );
}
